import React, { useEffect, useRef, useState } from 'react';

/**
 * Числовое поле для настроек и форм без двух болячек type="number":
 *
 *  - «020»: при значении 0 и вводе «2» браузер даёт «02», парсер — 2, а React
 *    не перезаписывает DOM, потому что "02" == 2 — ведущий ноль остаётся и
 *    копится. Здесь поле текстовое, в стейте строка, ведущие нули срезаются.
 *  - ноль не стереть: `onChange={… parseFloat(v) || 0}` возвращал 0 на каждое
 *    стирание. Здесь пустая строка допустима во время ввода, а наружу число
 *    уходит только когда его можно разобрать; при уходе фокуса пустое поле
 *    заменяется на `fallback`, значение зажимается в [min, max].
 *
 * Наружу (`onChange`) всегда уходит число — родительский стейт остаётся
 * числовым, обработчики «Сохранить» не меняются.
 */
export default function NumInput({
  value, onChange, fallback = 0, integer = false, min, max, allowNegative,
  onFocus, onBlur, ...rest
}) {
  const negative = allowNegative != null ? allowNegative : (min != null && min < 0);
  const toText = (v) => (v == null || v === '' || Number.isNaN(v) ? '' : String(v));
  const [text, setText] = useState(toText(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(toText(value));
  }, [value]);

  const parse = (s) => {
    const str = String(s).replace(',', '.');
    const n = integer ? parseInt(str, 10) : parseFloat(str);
    return Number.isFinite(n) ? n : null;
  };
  const clamp = (n) => {
    let v = n;
    if (min != null && v < min) v = min;
    if (max != null && v > max) v = max;
    return v;
  };
  const pattern = integer
    ? (negative ? /^-?\d*$/ : /^\d*$/)
    : (negative ? /^-?\d*([.,]\d*)?$/ : /^\d*([.,]\d*)?$/);

  return (
    <input
      {...rest}
      type="text"
      inputMode={integer ? 'numeric' : 'decimal'}
      value={text}
      onFocus={(e) => { focused.current = true; if (onFocus) onFocus(e); }}
      onChange={(e) => {
        let raw = e.target.value;
        if (!pattern.test(raw)) return;
        raw = raw.replace(/^(-?)0+(?=\d)/, '$1');
        setText(raw);
        const n = parse(raw);
        if (n != null) {
          const v = clamp(n);
          if (v !== value) onChange(v);
        }
      }}
      onBlur={(e) => {
        focused.current = false;
        let n = parse(text);
        if (n == null) n = fallback;
        n = clamp(n);
        setText(toText(n));
        if (n !== value) onChange(n);
        if (onBlur) onBlur(e);
      }}
    />
  );
}
